/* =============================================================================
   GUAC-BOT customer-service chat widget (guac-chat-2)
   -----------------------------------------------------------------------------
   Floating bubble (replaces native Shopify Inbox). Home screen offers:
     • Ask GUAC-BOT      — streams a Claude reply from POST /api/guac-bot/chat
     • Talk to a human   — reach Ryan & Liza (POST /api/guac-bot/request-human)
     • Your recent conversations — resume any past thread; back button returns home
   A "Talk to a real person" button is always available inside a bot chat.

   ⚠️ A CONVERSATION WITH A PERSON FOLLOWS THE CUSTOMER AROUND THE SITE (2026-10-27).
   The widget used to join its conversation's socket room only while that chat was
   on screen, and never re-joined after a page change or a reconnect — so a staff
   reply sent a minute after the customer clicked to another page reached nobody,
   staff could not tell whether anyone was there, and the reply was auto-emailed.
   Now the ACTIVE human conversation (localStorage `guac_cs_active`) is re-joined on
   every page load and every socket (re)connect; the widget reports presence
   (cs-presence: panel open on this conversation? tab visible?) and read receipts
   (cs-seen: the newest staff message actually DISPLAYED); a staff reply that lands
   while the chat is closed puts a COUNT on the bubble, and opening the bubble with
   unread replies goes straight to that conversation. Follow-up messages go to
   /api/guac-bot/message, not back through request-human (which re-texted staff on
   every message and refused the 4th within five minutes).

   Identity: sends X-Customer-Key (window.MHV_CUSTOMER_KEY) when logged in.
   Reads customer info from window.MHV_CS_CONFIG (injected by guac-chat.liquid).
   Suppressed on the live-auction page (its own docked chat) and admin pages.
   ============================================================================= */
(function () {
  'use strict';

  // ⚠️ AN ADMIN PAGE IS RECOGNISED BY THE PAGE, NOT BY A LIST OF PATHS.
  //
  // This was a path list alone, and a list of pages is wrong the moment somebody
  // adds one: the customer-service bubble was turning up on Packing
  // (`/pages/fulfillment`, the drawer's own link), the Session Explorer, Tasks,
  // Penney Panel, the Customer Manager, the Contract Builder and all five hosted
  // app pages — every admin page built after the list was written. On a staff
  // screen it is at best furniture and at worst a cashier accidentally opening a
  // conversation with our own bot in front of a customer.
  //
  // Every gated page renders `#mhv-admin-gate-content` (21 sections do, through
  // `snippets/mhv-admin-gate.liquid`), and the gate only ever HIDES that element,
  // never removes it — so the test holds whether or not the gate has let you in.
  // This script is `defer`red and the element is in the parsed document before
  // it runs; it is the same test the site-wide pop-up in theme.liquid makes.
  if (document.getElementById('mhv-admin-gate-content')) return;

  // The path list stays as well, for the admin-ish pages that carry NO gate —
  // the projector and the customer display are public by design, and the live
  // page has its own docked chat.
  var ADMIN_PATHS = ['/pages/host', '/pages/invoices', '/pages/marketing', '/pages/orders',
    '/pages/fulfillment', '/pages/catalogue-manager', '/pages/consignment-manager',
    '/pages/customer-manager', '/pages/contract-builder', '/pages/reg-check', '/pages/concessions',
    '/pages/stats', '/pages/session-explorer', '/pages/scheduler', '/pages/database',
    '/pages/employees', '/pages/absentee-bids', '/pages/photo-upload', '/pages/app-logs',
    '/pages/utilities', '/pages/tasks', '/pages/penney-panel', '/pages/cs-inbox',
    '/pages/calendar', '/pages/trip-log', '/pages/brain', '/pages/life-safety',
    '/pages/projector', '/pages/customer-display'];
  var path = (window.location.pathname || '').toLowerCase();
  if (ADMIN_PATHS.some(function (p) { return path.indexOf(p) === 0; })) return;
  if (document.getElementById('custom-chat-container') || window.MHV_LIVE_CONFIG) return;

  var CONFIG = window.MHV_CS_CONFIG || {};
  var SERVER = (window.MHV && window.MHV.SERVER_URL) || CONFIG.serverUrl || '';
  if (!SERVER) return;
  var LIST_KEY = 'guac_cs_threads';
  var ACTIVE_KEY = 'guac_cs_active';   // { publicId, lastSeenStaffId } — the human conversation we keep listening to
  var GUEST_KEY = 'guac_cs_guest';     // { email, name } a guest left so we can reply (2026-10-29)

  // ── utils ──
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  function el(tag, cls, html) { var e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; }
  function customerKey() { return window.MHV_CUSTOMER_KEY || null; }
  function headers() { var h = { 'Content-Type': 'application/json' }; var k = customerKey(); if (k) h['X-Customer-Key'] = k; return h; }

  // Minimal, safe markdown → HTML (escape first, then a limited inline/block set)
  // so GUAC-BOT's **bold**, *italics*, `code`, links and bullet lists render.
  function mdInline(raw) {
    var s = esc(raw);
    s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
    s = s.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/__([^_]+?)__/g, '<strong>$1</strong>');
    s = s.replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*/g, '$1<em>$2</em>');
    // The URL is placed in a QUOTED attribute; esc() above only escapes & < > (not quotes),
    // and the URL char-class allows a literal " , so a bot-rendered link (Claude output can
    // be steered by customer prompt-injection) could break out of href="..." → attribute-
    // injection XSS. Escape the URL's quotes for attribute context. (deep-audit #9.)
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, function (_m, text, url) {
      var hrefSafe = url.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      return '<a href="' + hrefSafe + '" target="_blank" rel="noopener noreferrer">' + text + '</a>';
    });
    return s;
  }
  function renderMarkdown(raw) {
    var lines = String(raw || '').split('\n');
    var html = '', para = [], list = [];
    function flushPara() { if (para.length) { html += '<p>' + mdInline(para.join('\n')).replace(/\n/g, '<br>') + '</p>'; para = []; } }
    function flushList() { if (list.length) { html += '<ul>' + list.map(function (li) { return '<li>' + mdInline(li) + '</li>'; }).join('') + '</ul>'; list = []; } }
    lines.forEach(function (ln) {
      var m = ln.match(/^\s*[-*]\s+(.*)$/);
      if (m) { flushPara(); list.push(m[1]); }
      else if (ln.trim() === '') { flushPara(); flushList(); }
      else { flushList(); para.push(ln); }
    });
    flushPara(); flushList();
    return '<div class="gc-md">' + html + '</div>';
  }
  function formatBody(sender, text) {
    return (sender === 'bot' || sender === 'staff') ? renderMarkdown(text) : esc(text);
  }

  function getThreads() { try { return JSON.parse(localStorage.getItem(LIST_KEY) || '[]'); } catch (e) { return []; } }
  function saveThreadEntry(entry) {
    var list = getThreads().filter(function (t) { return t.publicId !== entry.publicId; });
    list.unshift(entry);
    try { localStorage.setItem(LIST_KEY, JSON.stringify(list.slice(0, 25))); } catch (e) {}
  }
  function touchThread(publicId, patch) {
    var list = getThreads();
    for (var i = 0; i < list.length; i++) { if (list[i].publicId === publicId) { Object.assign(list[i], patch, { at: Date.now() }); break; } }
    try { localStorage.setItem(LIST_KEY, JSON.stringify(list)); } catch (e) {}
  }

  /* ⚠️ A GUEST LEAVES AN EMAIL BEFORE THEIR FIRST MESSAGE (owner, 2026-10-29).
     A question from someone we cannot see and cannot write to can only be answered
     while they are still on the page. Logged in = we already have their email; a
     guest is asked once (remembered on this browser) and can log in instead. The
     server refuses a guest message with no email (`needContact`) — that is what
     makes an older open conversation ask too. */
  var guestMem = null;
  function getGuest() {
    if (guestMem) return guestMem;
    try { var g = JSON.parse(localStorage.getItem(GUEST_KEY) || 'null'); if (g && g.email) guestMem = g; } catch (e) {}
    return guestMem;
  }
  function setGuest(g) { guestMem = g; try { if (g) localStorage.setItem(GUEST_KEY, JSON.stringify(g)); else localStorage.removeItem(GUEST_KEY); } catch (e) {} }
  // Who to write back to, when the server cannot tell from the customer key. A
  // logged-in customer whose key has not arrived yet sends their account email.
  function contact() {
    if (customerKey()) return null;
    var g = getGuest();
    if (g) return g;
    if (CONFIG.customer && CONFIG.customer.email) return { email: CONFIG.customer.email, name: CONFIG.customer.firstName || null };
    return null;
  }
  function needsContact() { return !customerKey() && !contact(); }
  var EMAIL_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[a-z]{2,}$/i;

  /* WHERE THEY ARE (2026-10-29): sent with every message, because a conversation
     crosses pages and "these" means whatever was on screen when THAT message was
     written. The hub's bid lightbox does not change the URL, so an open lightbox
     (it carries data-cs-title / data-cs-url) names the lot. */
  function pageContext() {
    var ctx = { path: (location.pathname || '/') + (location.search || ''), title: document.title || '' };
    try {
      var nodes = document.querySelectorAll('[data-cs-title]');
      for (var i = 0; i < nodes.length; i++) {
        if (window.getComputedStyle(nodes[i]).display !== 'none') {
          ctx.item = { title: nodes[i].getAttribute('data-cs-title') || '', path: nodes[i].getAttribute('data-cs-url') || '' };
          break;
        }
      }
    } catch (e) { /* context is a courtesy */ }
    return ctx;
  }
  function withContext(obj) {
    obj.page = pageContext();
    var c = contact();
    if (c) obj.guest = c;
    return obj;
  }

  function getActive() { try { return JSON.parse(localStorage.getItem(ACTIVE_KEY) || 'null'); } catch (e) { return null; } }
  function setActive(publicId, patch) {
    if (!publicId) return;
    var cur = getActive();
    var next = (cur && cur.publicId === publicId) ? cur : { publicId: publicId, lastSeenStaffId: 0 };
    if (patch) Object.assign(next, patch);
    try { localStorage.setItem(ACTIVE_KEY, JSON.stringify(next)); } catch (e) {}
    joinRoom(publicId);
  }
  function clearActive() { try { localStorage.removeItem(ACTIVE_KEY); } catch (e) {} }

  // ── state ──
  var threadPublicId = null;
  var mode = null;             // 'bot' | 'human' | null
  var sending = false;
  var socket = null;
  var rooms = {};              // publicId -> true: every conversation room this tab is in
  var seenMessageIds = {};
  var pendingSubject = null;
  var unread = 0;              // staff replies not yet seen (bubble badge)

  // ── DOM ──
  var root = el('div');
  root.id = 'guac-chat-root';
  root.innerHTML =
    '<button id="guac-chat-bubble" aria-label="Chat with us">' +
      '<span class="gc-badge" id="guac-chat-badge">1</span>' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>' +
    '</button>' +
    '<div id="guac-chat-panel" role="dialog" aria-label="Customer service chat">' +
      '<div class="gc-header">' +
        '<button class="gc-iconbtn gc-back" id="guac-chat-back" aria-label="Back" style="display:none">‹</button>' +
        '<div class="gc-avatar" id="guac-chat-avatar">G</div>' +
        '<div><div class="gc-title" id="guac-chat-title">Granite Union Auction</div>' +
        '<div class="gc-sub" id="guac-chat-sub">We usually reply fast</div></div>' +
        '<div class="gc-spacer"></div>' +
        '<button class="gc-iconbtn gc-expand" id="guac-chat-expand" aria-label="Expand"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg></button>' +
        '<button class="gc-iconbtn" id="guac-chat-close" aria-label="Close">&times;</button>' +
      '</div>' +
      '<div class="gc-body" id="guac-chat-body"></div>' +
      '<div class="gc-human-bar" id="guac-chat-humanbar" style="display:none"><button class="gc-human-btn" id="guac-chat-human">Talk to a real person (Ryan or Liza)</button></div>' +
      '<div class="gc-composer" id="guac-chat-composer" style="display:none">' +
        '<textarea id="guac-chat-input" rows="1" placeholder="Type your message…"></textarea>' +
        '<button class="gc-send" id="guac-chat-send" aria-label="Send">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>' +
        '</button>' +
      '</div>' +
      '<div class="gc-footnote" id="guac-chat-foot"></div>' +
    '</div>';
  document.body.appendChild(root);

  var bubble = root.querySelector('#guac-chat-bubble');
  var body = root.querySelector('#guac-chat-body');
  var composer = root.querySelector('#guac-chat-composer');
  var input = root.querySelector('#guac-chat-input');
  var sendBtn = root.querySelector('#guac-chat-send');
  var subEl = root.querySelector('#guac-chat-sub');
  var titleEl = root.querySelector('#guac-chat-title');
  var footEl = root.querySelector('#guac-chat-foot');
  var backBtn = root.querySelector('#guac-chat-back');
  var humanBar = root.querySelector('#guac-chat-humanbar');

  // ── rendering ──
  function scrollDown() { body.scrollTop = body.scrollHeight; }
  function clearBody() { body.innerHTML = ''; }

  function whoLabel(sender, senderName) {
    if (sender === 'bot') return 'GUAC-BOT';
    if (sender === 'staff') return senderName || 'Granite Union Auction';
    if (sender === 'customer') return 'You';
    return null;
  }

  function addMessage(sender, text, id, senderName) {
    if (id != null) { if (seenMessageIds[id]) return null; seenMessageIds[id] = true; }
    if (sender === 'staff' && id != null) maybeMarkSeen(threadPublicId, id);
    var m = el('div', 'gc-msg gc-from-' + sender);
    var who = whoLabel(sender, senderName);
    m.innerHTML = (who ? '<span class="gc-who">' + esc(who) + '</span>' : '') + formatBody(sender, text);
    body.appendChild(m);
    scrollDown();
    return m;
  }

  function statusLabel(s) {
    if (s === 'bot') return 'GUAC-BOT';
    if (s === 'closed') return 'Closed';
    return 'With Ryan & Liza';
  }

  // ── home screen ──
  function showHome() {
    var was = threadPublicId;
    mode = null;
    threadPublicId = null;
    leaveRoomUnlessActive(was);
    sendPresence();
    composer.style.display = 'none';
    humanBar.style.display = 'none';
    footEl.textContent = '';
    backBtn.style.display = 'none';
    titleEl.textContent = 'Granite Union Auction';
    subEl.textContent = 'We usually reply fast';
    clearBody();

    var wrap = el('div', 'gc-launch');
    wrap.innerHTML =
      '<h3>How can we help?</h3>' +
      '<p>Ask our assistant anything about bidding, consignment, or your account — or reach Ryan &amp; Liza directly.</p>';
    var b1 = el('button', 'gc-choice gc-primary', 'Ask GUAC-BOT<span class="gc-choice-sub">Instant answers on terms, pricing &amp; your account</span>');
    var b2 = el('button', 'gc-choice', 'Talk to a human<span class="gc-choice-sub">Message Ryan &amp; Liza — we’ll reply here &amp; by email</span>');
    b1.addEventListener('click', function () { newConversation('bot'); });
    b2.addEventListener('click', function () { newConversation('human'); });
    wrap.appendChild(b1);
    wrap.appendChild(b2);
    var g = !customerKey() && getGuest();
    if (g) {
      var as = el('div', 'gc-guest-as', 'We’ll reply to <strong>' + esc(g.email) + '</strong> ');
      var chg = el('button', 'gc-linkbtn', 'Change');
      chg.addEventListener('click', function () { setGuest(null); showHome(); });
      as.appendChild(chg);
      wrap.appendChild(as);
    }
    body.appendChild(wrap);

    renderRecent(wrap);
  }

  function renderRecent(wrap) {
    var local = getThreads();
    var render = function (threads) {
      if (!threads.length) return;
      var box = el('div', 'gc-recent');
      box.appendChild(el('div', 'gc-recent-h', 'Your recent conversations'));
      threads.slice(0, 6).forEach(function (t) {
        var item = el('button', 'gc-recent-item',
          '<span class="gc-ri-ic"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg></span>' +
          '<span class="gc-ri-meta"><span class="gc-ri-subj">' + esc(t.subject || 'Conversation') + '</span>' +
          '<span class="gc-ri-status">' + esc(statusLabel(t.status)) + '</span></span>');
        item.addEventListener('click', function () { openConversation(t.publicId); });
        box.appendChild(item);
      });
      wrap.appendChild(box);
    };

    if (customerKey()) {
      fetch(SERVER + '/api/guac-bot/my-threads', { headers: headers() })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          var server = (data && data.threads) || [];
          var byId = {};
          local.forEach(function (t) { byId[t.publicId] = { publicId: t.publicId, subject: t.subject, status: t.status, at: t.at || 0 }; });
          server.forEach(function (t) {
            byId[t.publicId] = { publicId: t.publicId, subject: t.subject || (byId[t.publicId] && byId[t.publicId].subject) || 'Conversation', status: t.status, at: new Date(t.lastMessageAt || 0).getTime() };
          });
          var merged = Object.keys(byId).map(function (k) { return byId[k]; }).sort(function (a, b) { return b.at - a.at; });
          render(merged);
        })
        .catch(function () { render(local); });
    } else {
      render(local);
    }
  }

  function setHeaderFor(newMode) {
    if (newMode === 'human') { titleEl.textContent = 'Ryan & Liza'; subEl.textContent = 'We’ll reply here & by email'; }
    else { titleEl.textContent = 'GUAC-BOT'; subEl.textContent = 'Usually instant'; }
  }

  function enterConversation(newMode) {
    mode = newMode;
    composer.style.display = 'flex';
    humanBar.style.display = newMode === 'bot' ? 'block' : 'none';
    backBtn.style.display = '';
    footEl.innerHTML = newMode === 'bot' ? 'GUAC-BOT can make mistakes — confirm anything important with Ryan or Liza.' : '';
    setHeaderFor(newMode);
    setTimeout(function () { input.focus(); }, 50);
  }

  function newConversation(startMode) {
    var was = threadPublicId;
    threadPublicId = null;
    seenMessageIds = {};
    pendingSubject = null;
    leaveRoomUnlessActive(was);
    sendPresence();
    clearBody();
    enterConversation(startMode);
    if (startMode === 'bot') {
      addMessage('bot', "Hi! I’m GUAC-BOT. Ask me about bidding, buyer’s premium, consignment, shipping, or — if you’re logged in — your order status, bidding, and order history. What can I help with?");
    } else {
      addMessage('system', "You’re reaching Ryan & Liza. Send your message below — we’ll get notified and reply right here (and by email if you step away).");
    }
    if (needsContact()) askContact(null);
  }

  // The email step: a card in the conversation, the composer hidden until it is
  // done. `then` runs afterwards (resending a message the server refused).
  function askContact(then) {
    var old = body.querySelector('.gc-contact');
    if (old) old.parentNode.removeChild(old);
    composer.style.display = 'none';
    var back = encodeURIComponent((location.pathname || '/') + (location.search || ''));
    var card = el('div', 'gc-contact');
    card.innerHTML =
      '<div class="gc-contact-h">How can we reach you?</div>' +
      '<p>So we can answer even if you leave the page, add your email — or <a href="/account/login?return_url=' + back + '">log in</a>.</p>' +
      '<label>Email<input type="email" class="gc-contact-email" autocomplete="email" inputmode="email" placeholder="you@example.com"></label>' +
      '<label>Name <span class="gc-opt">(optional)</span><input type="text" class="gc-contact-name" autocomplete="name" maxlength="80"></label>' +
      '<div class="gc-contact-err" role="alert"></div>' +
      '<button type="button" class="gc-contact-go">Continue</button>';
    body.appendChild(card);
    scrollDown();
    var emailIn = card.querySelector('.gc-contact-email');
    var nameIn = card.querySelector('.gc-contact-name');
    var err = card.querySelector('.gc-contact-err');
    var prev = getGuest() || (CONFIG.customer && CONFIG.customer.email ? { email: CONFIG.customer.email, name: CONFIG.customer.firstName } : null);
    if (prev) { emailIn.value = prev.email || ''; nameIn.value = prev.name || ''; }
    function go() {
      var email = emailIn.value.trim();
      if (!EMAIL_RE.test(email)) { err.textContent = 'Please enter a valid email address.'; emailIn.focus(); return; }
      setGuest({ email: email.toLowerCase(), name: nameIn.value.trim() || null });
      if (card.parentNode) card.parentNode.removeChild(card);
      composer.style.display = 'flex';
      if (then) then(); else setTimeout(function () { input.focus(); }, 50);
    }
    card.querySelector('.gc-contact-go').addEventListener('click', go);
    [emailIn, nameIn].forEach(function (n) { n.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); go(); } }); });
    setTimeout(function () { emailIn.focus(); }, 50);
  }
  // The server said it has no way to reach this guest: ask, then try again.
  function retryAfterContact(fn, text) {
    askContact(function () { sending = true; sendBtn.disabled = true; fn(text); });
  }

  function openConversation(publicId) {
    seenMessageIds = {};
    fetch(SERVER + '/api/guac-bot/thread/' + encodeURIComponent(publicId), { headers: headers() })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.thread) { showHome(); return; }
        threadPublicId = publicId;
        clearBody();
        (data.messages || []).forEach(function (m) { addMessage(m.sender, m.body, m.id, m.senderName); });
        mode = data.thread.status === 'bot' ? 'bot' : 'human';
        enterConversation(mode);
        touchThread(publicId, { status: data.thread.status });
        if (mode === 'human') setActive(publicId);
        joinRoom(publicId);
        // Everything just drawn has been seen (when the panel is open + visible).
        var lastStaff = 0;
        (data.messages || []).forEach(function (m) { if (m.sender === 'staff' && m.id > lastStaff) lastStaff = m.id; });
        if (lastStaff) maybeMarkSeen(publicId, lastStaff);
        clearUnreadFor(publicId);
        sendPresence();
      })
      .catch(function () { showHome(); });
  }

  // Escalate the current bot conversation to a person at any time.
  function talkToHuman() {
    if (mode === 'human') return;
    if (!threadPublicId) { newConversation('human'); return; }
    mode = 'human';
    humanBar.style.display = 'none';
    setHeaderFor('human');
    footEl.innerHTML = '';
    addMessage('system', "Connecting you with Ryan & Liza — they’ll reply here soon (and by email if you step away).");
    requestHumanNow();
  }
  function requestHumanNow() {
    fetch(SERVER + '/api/guac-bot/request-human', {
      method: 'POST', headers: headers(),
      body: JSON.stringify(withContext({ threadPublicId: threadPublicId, reason: 'Customer tapped “talk to a real person”' }))
    }).then(function (r) { return r.json().catch(function () { return {}; }).then(function (data) { return { r: r, data: data }; }); })
      .then(function (x) {
        if (x.r.status === 400 && x.data && x.data.needContact) { askContact(requestHumanNow); return; }
        var data = x.data;
        if (data && data.thread) { touchThread(data.thread.publicId, { status: 'needs_human' }); setActive(data.thread.publicId); }
      })
      .catch(function () {});
  }

  // ── socket ──
  // socket.io is loaded ASYNC from the snippet, so the first call can come before
  // window.io exists; keep trying for a while rather than giving up for the page.
  var socketWaits = 0;
  function ensureSocket() {
    if (socket) return socket;
    if (typeof window.io === 'undefined') {
      if (socketWaits++ < 40) setTimeout(function () { if (!socket && ensureSocket()) rejoinAll(); }, 500);
      return null;
    }
    try { socket = window.socket || window.io(SERVER, { transports: ['websocket', 'polling'], reconnection: true }); }
    catch (e) { return null; }
    // A reconnect (dyno restart, deploy, a phone waking up) drops every room on the
    // server side. Re-join them, or staff replies stop arriving and we read as gone.
    socket.on('connect', rejoinAll);
    socket.on('cs-message', function (m) {
      if (!m || (m.sender !== 'staff' && m.sender !== 'system')) return;
      var target = m.publicId || threadPublicId;
      var showing = target && target === threadPublicId;
      if (showing) addMessage(m.sender, m.body, m.id, m.senderName);
      if (m.sender === 'staff') {
        if (target) setActive(target);
        if (!showing || !panelVisible()) bumpUnread();
        // Sitting on the home screen with the panel open: take them to the reply.
        if (!showing && !threadPublicId && target && root.classList.contains('gc-open')) openConversation(target);
      }
    });
    socket.on('cs-status', function (s) {
      if (!s || !s.status) return;
      if (s.publicId) touchThread(s.publicId, { status: s.status });
      if (s.publicId && s.publicId !== threadPublicId) return;
      if (s.status !== 'bot') { mode = 'human'; humanBar.style.display = 'none'; subEl.textContent = 'Ryan & Liza are on it'; if (threadPublicId) setActive(threadPublicId); }
    });
    if (socket.connected) rejoinAll();
    return socket;
  }
  function panelVisible() { return root.classList.contains('gc-open') && document.visibilityState !== 'hidden'; }
  function flagsFor(publicId) {
    return { publicId: publicId, open: !!(panelVisible() && threadPublicId === publicId), visible: document.visibilityState !== 'hidden' };
  }
  function rejoinAll() {
    if (!socket) return;
    Object.keys(rooms).forEach(function (id) { socket.emit('cs-join', flagsFor(id)); });
  }
  function joinRoom(publicId) {
    if (!publicId) return;
    var already = !!rooms[publicId];
    rooms[publicId] = true;
    var s = ensureSocket();
    if (s && !already) s.emit('cs-join', flagsFor(publicId));
    else if (s) s.emit('cs-presence', flagsFor(publicId));
  }
  function leaveRoom(publicId) {
    if (!publicId || !rooms[publicId]) return;
    delete rooms[publicId];
    if (socket) { try { socket.emit('cs-leave', { publicId: publicId }); } catch (e) {} }
  }
  // Leaving a conversation's SCREEN is not leaving the conversation: the active
  // human one stays joined so a reply still arrives.
  function leaveRoomUnlessActive(publicId) {
    var a = getActive();
    if (publicId && !(a && a.publicId === publicId)) leaveRoom(publicId);
  }
  function sendPresence() {
    if (!socket) return;
    Object.keys(rooms).forEach(function (id) { try { socket.emit('cs-presence', flagsFor(id)); } catch (e) {} });
  }
  // Read receipt: only for what is actually on screen.
  function maybeMarkSeen(publicId, messageId) {
    if (!publicId || !messageId || !panelVisible() || publicId !== threadPublicId) return;
    var a = getActive();
    if (a && a.publicId === publicId && (a.lastSeenStaffId || 0) < messageId) {
      a.lastSeenStaffId = messageId;
      try { localStorage.setItem(ACTIVE_KEY, JSON.stringify(a)); } catch (e) {}
    }
    if (socket && rooms[publicId]) { try { socket.emit('cs-seen', { publicId: publicId, messageId: messageId }); } catch (e) {} }
  }
  function markShownSeen() {
    var max = 0;
    Object.keys(seenMessageIds).forEach(function (k) { var n = parseInt(k, 10); if (n > max) max = n; });
    if (max) maybeMarkSeen(threadPublicId, max);
  }
  function setUnread(n) {
    unread = Math.max(0, n | 0);
    var b = root.querySelector('#guac-chat-badge');
    if (!b) return;
    b.textContent = unread > 9 ? '9+' : String(unread || 1);
    b.style.display = unread > 0 ? 'flex' : 'none';
    bubble.setAttribute('aria-label', unread > 0 ? 'Chat with us — ' + unread + ' new repl' + (unread === 1 ? 'y' : 'ies') : 'Chat with us');
  }
  function bumpUnread() { setUnread(unread + 1); }
  function clearUnreadFor(publicId) {
    var a = getActive();
    if (!a || a.publicId === publicId) setUnread(0);
  }

  // ── sending ──
  function autosize() { input.style.height = 'auto'; input.style.height = Math.min(input.scrollHeight, 110) + 'px'; }

  function send() {
    var text = input.value.trim();
    if (!text || sending) return;
    sending = true;
    sendBtn.disabled = true;
    if (!threadPublicId && !pendingSubject) pendingSubject = text.slice(0, 80);
    addMessage('customer', text);
    input.value = '';
    autosize();
    if (mode === 'human') return sendHuman(text);
    return sendBot(text);
  }

  function persistThread(id) {
    if (id && id !== threadPublicId) {
      threadPublicId = id;
      saveThreadEntry({ publicId: id, subject: pendingSubject || 'Conversation', status: mode, at: Date.now() });
      joinRoom(id);
    } else if (id) { touchThread(id, { status: mode }); }
    if (id && mode === 'human') setActive(id);
    sendPresence();
  }

  function sendHuman(text) {
    // A conversation that already exists takes the message as a FOLLOW-UP; only
    // the first message of a brand-new human conversation goes through
    // request-human (which creates the thread and alerts staff).
    if (threadPublicId) return sendFollowUp(text);
    fetch(SERVER + '/api/guac-bot/request-human', {
      method: 'POST', headers: headers(),
      body: JSON.stringify(withContext({ threadPublicId: threadPublicId, message: text, reason: 'Customer message via widget' }))
    }).then(function (r) { return r.json().catch(function () { return {}; }).then(function (data) { return { r: r, data: data }; }); })
      .then(function (x) {
        if (x.r.status === 400 && x.data && x.data.needContact) { retryAfterContact(sendHuman, text); return; }
        if (!x.r.ok) throw new Error((x.data && x.data.error) || 'failed');
        var data = x.data;
        if (data && data.thread) persistThread(data.thread.publicId);
        var c = contact();
        addMessage('system', "Got it — Ryan & Liza have been notified and will reply here soon" + (c ? " (or by email to " + c.email + ")." : "."));
      })
      .catch(function () { addMessage('system', "Sorry, that didn’t go through. Please try again or text us at (603) 451-6960."); })
      .then(function () { sending = false; sendBtn.disabled = false; });
  }

  function sendFollowUp(text) {
    var id = threadPublicId;
    fetch(SERVER + '/api/guac-bot/message', {
      method: 'POST', headers: headers(),
      body: JSON.stringify(withContext({ threadPublicId: id, message: text }))
    }).then(function (r) {
      if (r.status === 404) { threadPublicId = null; return sendHumanFresh(text); }
      return r.json().then(function (data) {
        if (r.status === 400 && data && data.needContact) { retryAfterContact(sendFollowUp, text); return; }
        if (!r.ok) throw new Error((data && data.error) || 'failed');
        if (data && data.thread) { touchThread(id, { status: data.thread.status }); setActive(id); }
      });
    }).catch(function () { addMessage('system', "Sorry, that didn’t go through. Please try again or text us at (603) 451-6960."); })
      .then(function () { sending = false; sendBtn.disabled = false; });
  }
  function sendHumanFresh(text) { return sendHuman(text); }

  function sendBot(text) {
    var typing = el('div', 'gc-typing', 'GUAC-BOT is thinking…');
    body.appendChild(typing); scrollDown();
    var botMsg = null, acc = '';
    function ensureBotMsg() { if (!botMsg) { if (typing.parentNode) typing.parentNode.removeChild(typing); botMsg = addMessage('bot', ''); } }

    fetch(SERVER + '/api/guac-bot/chat', {
      method: 'POST', headers: headers(),
      body: JSON.stringify(withContext({ threadPublicId: threadPublicId, message: text }))
    }).then(function (resp) {
      if (resp.status === 400) {
        return resp.json().catch(function () { return {}; }).then(function (d) {
          if (!(d && d.needContact)) throw new Error('bad request');
          if (typing.parentNode) typing.parentNode.removeChild(typing);
          retryAfterContact(sendBot, text);
        });
      }
      if (!resp.ok || !resp.body) throw new Error('bad response');
      var reader = resp.body.getReader(), decoder = new TextDecoder(), buf = '';
      function pump() {
        return reader.read().then(function (res) {
          if (res.done) return finish();
          buf += decoder.decode(res.value, { stream: true });
          var chunks = buf.split('\n\n'); buf = chunks.pop();
          chunks.forEach(function (c) { handleEvent(c); });
          return pump();
        });
      }
      return pump();
    }).catch(function () {
      ensureBotMsg();
      botMsg.innerHTML = '<span class="gc-who">GUAC-BOT</span>' + renderMarkdown("Sorry — I hit a snag. You can try again, or tap **Talk to a real person** below.");
    }).then(function () { finish(); });

    function handleEvent(chunk) {
      var ev = null, dataStr = '';
      chunk.split('\n').forEach(function (line) {
        if (line.indexOf('event:') === 0) ev = line.slice(6).trim();
        else if (line.indexOf('data:') === 0) dataStr += line.slice(5).trim();
      });
      var data = {}; try { data = dataStr ? JSON.parse(dataStr) : {}; } catch (e) {}
      if (ev === 'thread') { persistThread(data.publicId); }
      else if (ev === 'text_delta') { ensureBotMsg(); acc += data.text || ''; botMsg.innerHTML = '<span class="gc-who">GUAC-BOT</span>' + renderMarkdown(acc); scrollDown(); }
      else if (ev === 'done') { if (data.status && data.status !== 'bot') { mode = 'human'; humanBar.style.display = 'none'; subEl.textContent = 'Ryan & Liza are on it'; if (threadPublicId) touchThread(threadPublicId, { status: data.status }); } }
      else if (ev === 'error') { ensureBotMsg(); }
    }

    var finished = false;
    function finish() { if (finished) return; finished = true; if (typing.parentNode) typing.parentNode.removeChild(typing); sending = false; sendBtn.disabled = false; input.focus(); }
  }

  // ── open / close / expand ──
  function open() {
    root.classList.add('gc-open');
    var active = getActive();
    // Unread replies from a person: go straight to that conversation.
    if (unread > 0 && active && active.publicId && active.publicId !== threadPublicId) {
      openConversation(active.publicId);
    } else if (!body.querySelector('.gc-msg') && !body.querySelector('.gc-launch')) {
      showHome();
    } else if (threadPublicId) {
      clearUnreadFor(threadPublicId);
      markShownSeen();
    }
    sendPresence();
  }
  function close() { root.classList.remove('gc-open'); sendPresence(); }

  bubble.addEventListener('click', open);
  root.querySelector('#guac-chat-close').addEventListener('click', close);
  root.querySelector('#guac-chat-expand').addEventListener('click', function () { root.classList.toggle('gc-expanded'); });
  root.querySelector('#guac-chat-human').addEventListener('click', talkToHuman);
  backBtn.addEventListener('click', showHome);
  sendBtn.addEventListener('click', send);
  input.addEventListener('input', autosize);
  input.addEventListener('keydown', function (e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } });

  if (window.MHV_CUSTOMER_KEY_READY && typeof window.MHV_CUSTOMER_KEY_READY.then === 'function') {
    window.MHV_CUSTOMER_KEY_READY.then(function () {}).catch(function () {});
  }

  // Public API so other surfaces (e.g. the account pickup UI / a notification) can open
  // the chat — optionally starting a fresh GUAC-BOT conversation with a prefilled message.
  function askBot(text) {
    open();
    newConversation('bot');
    if (text) { input.value = String(text); try { autosize(); } catch (e) { /* ignore */ } }
    setTimeout(function () { try { input.focus(); } catch (e) { /* ignore */ } }, 60);
  }
  window.GuacChat = { open: open, close: close, ask: askBot };
  // Any link marked data-guac-chat-open opens the chat instead of following its href
  // (the footer's "Send us a message", 2026-10-31). The href stays a real fallback for
  // the pages where this script returns early and the chat never loads.
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('[data-guac-chat-open]');
    if (!a) return;
    e.preventDefault();
    open();
  });

  // ── restore the active human conversation on every page ──
  // Re-join its room (so a staff reply arrives live and staff can see we are on
  // the site) and count staff replies we have not displayed yet.
  function restoreActive() {
    var active = getActive();
    if (!active || !active.publicId) return;
    joinRoom(active.publicId);
    fetch(SERVER + '/api/guac-bot/thread/' + encodeURIComponent(active.publicId), { headers: headers() })
      .then(function (r) {
        if (r.status === 404) {
          // A logged-in customer whose key never arrived is not proof the thread is
          // gone (a claimed thread reads 404 without the key) — keep it for next time.
          if (customerKey() || !CONFIG.loggedIn) { clearActive(); leaveRoom(active.publicId); }
          return null;
        }
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        if (!data || !data.thread) return;
        var seenUpTo = (getActive() || {}).lastSeenStaffId || 0;
        var n = 0;
        (data.messages || []).forEach(function (m) { if (m.sender === 'staff' && m.id > seenUpTo) n++; });
        touchThread(active.publicId, { status: data.thread.status });
        if (!n && data.thread.status === 'closed') { clearActive(); leaveRoom(active.publicId); return; }
        if (n) setUnread(n);
      })
      .catch(function () {});
  }
  // A claimed conversation is only readable with the customer key, which is
  // provisioned asynchronously — wait for it (briefly) when it is on its way.
  if (window.MHV_CUSTOMER_KEY_READY && typeof window.MHV_CUSTOMER_KEY_READY.then === 'function' && !customerKey()) {
    var restored = false;
    var go = function () { if (!restored) { restored = true; restoreActive(); } };
    window.MHV_CUSTOMER_KEY_READY.then(go, go);
    setTimeout(go, 4000);
  } else {
    restoreActive();
  }
  document.addEventListener('visibilitychange', function () {
    sendPresence();
    if (document.visibilityState === 'visible' && root.classList.contains('gc-open') && threadPublicId) { clearUnreadFor(threadPublicId); markShownSeen(); }
  });
})();
